import { FC, useCallback, useRef } from 'react';

import { ForwardProps } from '../../../../types';
import { useI18n, useFocusWithin } from '../../../../hooks';
import { msToTimeFormat, timeFormatToMs } from '../utils';
import StyledDateTime from '../DateTime.styles';
import { StyledFormControl } from '../../../FormControl';
import Flex from '../../../Flex';
import PartInput from '../PartInput';
import useAutoFocusNextInput from '../useAutoFocusNextInput';

import DurationInputProps from './DurationInput.types';

type TimeProps = DurationInputProps & ForwardProps;

const Time: FC<TimeProps> = (props: DurationInputProps) => {
  const { value, onChange, disabled, readOnly, required, status, autoFocus, ...restProps } = props;

  const time =
    value !== undefined
      ? msToTimeFormat(value)
      : { hour: undefined, minute: undefined, second: undefined };

  const [hourInputRef, minuteInputRef, secondInputRef] = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null)
  ];

  useAutoFocusNextInput([hourInputRef, minuteInputRef, secondInputRef]);

  const pickParts = () => {
    return [
      hourInputRef.current?.value || undefined,
      minuteInputRef.current?.value || undefined,
      secondInputRef.current?.value || undefined
    ];
  };

  const onFocusChange = useCallback(
    (focused: boolean) => {
      if (!focused && !disabled && !readOnly && onChange) {
        const parts = pickParts();
        onChange(
          parts.some(v => v !== undefined)
            ? timeFormatToMs({
                hour: Number(parts[0] ?? 0),
                minute: Number(parts[1] ?? 0),
                second: Number(parts[2] ?? 0)
              })
            : undefined
        );
      }
    },
    [disabled, readOnly, onChange]
  );

  const t = useI18n();

  const containerRef = useRef<HTMLElement>(null);
  useFocusWithin([containerRef], onFocusChange);

  return (
    <Flex
      as={StyledDateTime}
      forwardedAs={StyledFormControl}
      container={{ alignItems: 'center', wrap: 'nowrap' }}
      status={status}
      readOnly={readOnly}
      disabled={disabled}
      ref={containerRef}
      onClick={(e: MouseEvent) => {
        if (e.target === containerRef.current) hourInputRef.current?.focus();
      }}
      {...restProps}
    >
      <PartInput
        key='hour'
        placeholder={t('hour_placeholder')}
        aria-label={t('hour_label_a11y')}
        value={time.hour}
        readOnly={readOnly}
        ref={hourInputRef}
        autoFocus={autoFocus}
      />
      :
      <PartInput
        key='minute'
        placeholder={t('minute_placeholder')}
        aria-label={t('minute_label_a11y')}
        value={time.minute}
        size={2}
        maxLength={2}
        readOnly={readOnly}
        max={59}
        ref={minuteInputRef}
      />
      :
      <PartInput
        key='second'
        placeholder={t('second_placeholder')}
        aria-label={t('second_label_a11y')}
        value={time.second}
        size={2}
        maxLength={2}
        readOnly={readOnly}
        max={59}
        ref={secondInputRef}
      />
    </Flex>
  );
};
export default Time;
